/**
 * URL contract for the Downloads tab. Search, filters and page all live in the query
 * string so a filtered view survives a reload, a share and the back button.
 */
export const DOWNLOAD_SEARCH_PARAM = 'q';
export const DOWNLOAD_ITEM_TYPE_PARAM = 'itemType';
export const DOWNLOAD_FILE_CATEGORY_PARAM = 'fileCategory';
export const DOWNLOAD_SORT_PARAM = 'sortBy';
export const DOWNLOAD_PAGE_PARAM = 'page';
export const DOWNLOAD_PAGE_SIZE_PARAM = 'pageSize';

/**
 * How the group list is ordered. Kept in step with the SortBy values the
 * professionalDownloadGroups query accepts; anything else falls back to A-Z.
 */
export const DOWNLOAD_SORT_OPTIONS = [
    { value: 'AZ', label: 'A - Z' },
    { value: 'ZA', label: 'Z - A' },
    { value: 'Files', label: 'Most files' },
] as const;

export const DEFAULT_DOWNLOAD_SORT = 'AZ';

const isDownloadSort = (value: string): boolean =>
    DOWNLOAD_SORT_OPTIONS.some((option) => option.value === value);

/**
 * How many groups one page of the accordion lists, from the shared pagination's
 * page-length options; anything else in the URL falls back to the default.
 */
export const DOWNLOAD_GROUP_PAGE_SIZE_OPTIONS = [12, 24, 48, 72];
export const DOWNLOAD_GROUP_PAGE_SIZE = 24;

/**
 * The item types a download can hang off. `Profile` is the professional's own page;
 * the rest are things it owns. Kept in step with the ItemType values the
 * professionalDownloadGroups query returns.
 */
export const DOWNLOAD_ITEM_TYPES = [
    'Profile',
    'Product',
    'Project',
    'Article',
] as const;

export type DownloadItemType = (typeof DOWNLOAD_ITEM_TYPES)[number];

export const DOWNLOAD_ITEM_TYPE_LABELS: Record<DownloadItemType, string> = {
    Profile: 'Company files',
    Product: 'Products',
    Project: 'Projects',
    Article: 'Articles',
};

export const isDownloadItemType = (value: string): value is DownloadItemType =>
    (DOWNLOAD_ITEM_TYPES as readonly string[]).includes(value);

/**
 * Reads the tab's filter state out of a URL. Unknown item types are dropped rather
 * than passed to the server, so a hand-edited link cannot widen the query.
 */
export const readDownloadFilters = (url: URL) => {
    const search = url.searchParams.get(DOWNLOAD_SEARCH_PARAM)?.trim() ?? '';
    const itemTypes = url.searchParams
        .getAll(DOWNLOAD_ITEM_TYPE_PARAM)
        .filter(isDownloadItemType);
    const fileCategories = url.searchParams
        .getAll(DOWNLOAD_FILE_CATEGORY_PARAM)
        .map((value) => value.trim())
        .filter(Boolean);
    const sortParam = url.searchParams.get(DOWNLOAD_SORT_PARAM) ?? '';
    const sort = isDownloadSort(sortParam) ? sortParam : DEFAULT_DOWNLOAD_SORT;
    const page = Math.max(
        1,
        Number(url.searchParams.get(DOWNLOAD_PAGE_PARAM) ?? '1') || 1,
    );
    const pageSizeParam = Number(
        url.searchParams.get(DOWNLOAD_PAGE_SIZE_PARAM),
    );
    const pageSize = DOWNLOAD_GROUP_PAGE_SIZE_OPTIONS.includes(pageSizeParam)
        ? pageSizeParam
        : DOWNLOAD_GROUP_PAGE_SIZE;

    return { search, itemTypes, fileCategories, sort, page, pageSize };
};

export type DownloadFilters = ReturnType<typeof readDownloadFilters>;
